/**
 * A2 — Document vocabulary dual-read (G2 / G22 / G23).
 *
 * Free-text `documents.category`, `documents.document_type` and
 * `documents.entity_type` have no CHECK constraint. The same concept is stored
 * under multiple casings (`Client` / `CLIENT` / `client`; `advance_care_directive`
 * / `Advance Care Directive`). Exact-string filters then miss rows at the point
 * of care — a clinical-safety P0, not a tidiness issue.
 *
 * This module is the single TypeScript source of truth for:
 *   - normalisation (case-fold, collapse space / underscore / hyphen)
 *   - alias → canonical UPPER_SNAKE resolution
 *   - known live (and workaround) variants for dual-read `.in()` queries
 *
 * The SQL migration seeds the same alias rows. A source-pin test fails if they
 * diverge. Free text remains authoritative until after the pilot freeze; shadow
 * columns are additive only.
 */

export type DocumentVocabularyKind = 'category' | 'document_type' | 'entity_type';

/**
 * Explicit bucket for values that do not map to a named concept. Never coerce.
 *
 * The token is `UNCLASSIFIED`, per the settled ruling (build plan prerequisite
 * P5): an unmapped value lands here and goes to a triage queue. A silent
 * coercion into a plausible-looking neighbour is the failure this whole slice
 * exists to prevent — it would make a mis-filed advance care directive look
 * correctly filed.
 */
export const DOCUMENT_VOCABULARY_UNCLASSIFIED = 'unclassified' as const;

export type DocumentVocabularyRegistryRow = {
  code: string;
  label: string;
  /** Subject / usage scope hint: client | worker | provider | any */
  applies_to: 'client' | 'worker' | 'provider' | 'any';
};

export type DocumentVocabularyAliasRow = {
  kind: DocumentVocabularyKind;
  /** Exact free-text variant as observed (or a known workaround token). */
  variant: string;
  /** Canonical lower_snake_case code, or the unclassified sentinel. */
  code: string;
};

// ─── Registries (canonical codes) ───────────────────────────────────────────

export const DOCUMENT_TYPE_REGISTRY: readonly DocumentVocabularyRegistryRow[] = [
  { code: 'unclassified', label: 'Unclassified (needs triage)', applies_to: 'any' },
  { code: 'clinical', label: 'Clinical', applies_to: 'client' },
  { code: 'clinical_document', label: 'Clinical document', applies_to: 'client' },
  { code: 'clinical_templates', label: 'Clinical templates', applies_to: 'provider' },
  { code: 'client', label: 'Client', applies_to: 'client' },
  { code: 'client_document', label: 'Client document', applies_to: 'client' },
  { code: 'compliance_audit', label: 'Compliance & audit', applies_to: 'provider' },
  { code: 'compliance', label: 'Compliance', applies_to: 'provider' },
  { code: 'compliance_document', label: 'Compliance document', applies_to: 'provider' },
  { code: 'audit', label: 'Audit', applies_to: 'provider' },
  { code: 'complaint', label: 'Complaint', applies_to: 'client' },
  { code: 'prudential', label: 'Prudential', applies_to: 'provider' },
  {
    code: 'prudential_compliance_statement',
    label: 'Prudential compliance statement',
    applies_to: 'provider',
  },
  { code: 'prudential_governance', label: 'Prudential governance', applies_to: 'provider' },
  {
    code: 'prudential_policy_artifact',
    label: 'Prudential policy artifact',
    applies_to: 'provider',
  },
  { code: 'prudential_evidence_pack', label: 'Prudential evidence pack', applies_to: 'provider' },
  { code: 'prudential_attestation', label: 'Prudential attestation', applies_to: 'provider' },
  {
    code: 'prudential_statement_package',
    label: 'Prudential statement package',
    applies_to: 'provider',
  },
  {
    code: 'annual_prudential_statement',
    label: 'Annual prudential statement',
    applies_to: 'provider',
  },
  {
    code: 'residential_accommodation_agreement',
    label: 'Residential accommodation agreement',
    applies_to: 'client',
  },
  {
    code: 'signed_accommodation_agreement',
    label: 'Signed accommodation agreement',
    applies_to: 'client',
  },
  { code: 'resident_agreement', label: 'Resident agreement', applies_to: 'client' },
  { code: 'residential_admission', label: 'Residential admission', applies_to: 'client' },
  {
    code: 'residential_admission_agreement',
    label: 'Residential admission agreement',
    applies_to: 'client',
  },
  { code: 'historical', label: 'Historical', applies_to: 'any' },
  { code: 'historical_consent', label: 'Historical consent', applies_to: 'client' },
  {
    code: 'historical_service_agreement',
    label: 'Historical service agreement',
    applies_to: 'client',
  },
  { code: 'assessment', label: 'Assessment', applies_to: 'client' },
  { code: 'home_risk_assessment', label: 'Home risk assessment', applies_to: 'client' },
  { code: 'funding', label: 'Funding', applies_to: 'client' },
  { code: 'care_plan', label: 'Care plan', applies_to: 'client' },
  { code: 'care_plan_active_version', label: 'Care plan active version', applies_to: 'client' },
  { code: 'care_plan_summary', label: 'Care plan summary', applies_to: 'client' },
  { code: 'care_plan_copy_issued', label: 'Care plan copy issued', applies_to: 'client' },
  { code: 'care_plan_participant_copy', label: 'Care plan participant copy', applies_to: 'client' },
  { code: 'care_plan_copy_evidence', label: 'Care plan copy evidence', applies_to: 'client' },
  // Owned by the head provider a brokered client is delivered under. Never this
  // provider's own care plan; own category so care_plan matches never include it.
  {
    code: 'head_provider_care_plan',
    label: 'Head provider care plan (owned by head provider)',
    applies_to: 'client',
  },
  { code: 'consent', label: 'Consent', applies_to: 'client' },
  { code: 'policies_and_procedures', label: 'Policies & procedures', applies_to: 'provider' },
  { code: 'policy', label: 'Policy', applies_to: 'provider' },
  { code: 'governance_policy', label: 'Governance policy', applies_to: 'provider' },
  { code: 'procedure', label: 'Procedure', applies_to: 'provider' },
  { code: 'worker_credential_renewal', label: 'Worker credential renewal', applies_to: 'worker' },
  { code: 'leave_request_evidence', label: 'Leave request evidence', applies_to: 'worker' },
  { code: 'leave_evidence', label: 'Leave evidence', applies_to: 'worker' },
  { code: 'whs', label: 'WHS', applies_to: 'provider' },
  { code: 'signed_document', label: 'Signed document', applies_to: 'any' },
  { code: 'contract', label: 'Contract', applies_to: 'provider' },
  { code: 'contractor_agreement', label: 'Contractor agreement', applies_to: 'provider' },
  { code: 'head_provider_agreement', label: 'Head provider agreement', applies_to: 'provider' },
  { code: 'vendor_contracts', label: 'Vendor contracts', applies_to: 'provider' },
  { code: 'sah_statement', label: 'SAH statement', applies_to: 'client' },
  { code: 'sah_monthly_statement', label: 'SAH monthly statement', applies_to: 'client' },
  { code: 'sah_budget_statement', label: 'SAH budget statement', applies_to: 'client' },
  { code: 'insurance', label: 'Insurance', applies_to: 'provider' },
  { code: 'vehicle_insurance', label: 'Vehicle insurance', applies_to: 'provider' },
  { code: 'professional_indemnity', label: 'Professional indemnity', applies_to: 'provider' },
  { code: 'financial', label: 'Financial', applies_to: 'provider' },
  { code: 'governance', label: 'Governance', applies_to: 'provider' },
  { code: 'statement', label: 'Statement', applies_to: 'any' },
  { code: 'invoice', label: 'Invoice', applies_to: 'provider' },
  { code: 'service_agreement', label: 'Service agreement', applies_to: 'client' },
  { code: 'respite_service_agreement', label: 'Respite service agreement', applies_to: 'client' },
  { code: 'chsp_service_agreement', label: 'CHSP service agreement', applies_to: 'client' },
  { code: 'human_resources', label: 'Human resources', applies_to: 'worker' },
  { code: 'quality_and_safety', label: 'Quality & safety', applies_to: 'provider' },
  { code: 'quality', label: 'Quality', applies_to: 'provider' },
  { code: 'registration', label: 'Registration', applies_to: 'provider' },
  { code: 'registration_certificate', label: 'Registration certificate', applies_to: 'provider' },
  { code: 'certificate', label: 'Certificate', applies_to: 'any' },
  { code: 'certification', label: 'Certification', applies_to: 'any' },
  { code: 'staff_credentials', label: 'Staff credentials', applies_to: 'worker' },
  { code: 'incident', label: 'Incident', applies_to: 'client' },
  { code: 'incident_report', label: 'Incident report', applies_to: 'client' },
  { code: 'incident_evidence', label: 'Incident evidence', applies_to: 'client' },
  { code: 'advance_care_directive', label: 'Advance care directive', applies_to: 'client' },
  { code: 'goals_of_care', label: 'Goals of care', applies_to: 'client' },
  { code: 'epoa', label: 'Enduring power of attorney', applies_to: 'client' },
  { code: 'behaviour_support_plan', label: 'Behaviour support plan', applies_to: 'client' },
  { code: 'wound_protocol', label: 'Wound protocol', applies_to: 'client' },
  { code: 'support_plan', label: 'Support plan', applies_to: 'client' },
  { code: 'individualised_budget', label: 'Individualised budget', applies_to: 'client' },
  {
    code: 'individualised_budget_active_version',
    label: 'Individualised budget active version',
    applies_to: 'client',
  },
  { code: 'sah_individualised_budget', label: 'SAH individualised budget', applies_to: 'client' },
  { code: 'report', label: 'Report', applies_to: 'any' },
  { code: 'final_report', label: 'Final report', applies_to: 'any' },
  { code: 'template', label: 'Template', applies_to: 'provider' },
  { code: 'sds', label: 'Safety data sheet', applies_to: 'provider' },
  { code: 'refund_deduction_evidence', label: 'Refund deduction evidence', applies_to: 'provider' },
  {
    code: 'external_submission_evidence',
    label: 'External submission evidence',
    applies_to: 'provider',
  },
  { code: 'remittance', label: 'Remittance', applies_to: 'provider' },
  {
    code: 'sirs_final_report_evidence',
    label: 'SIRS final report evidence',
    applies_to: 'provider',
  },
  { code: 'board_attestation', label: 'Board attestation', applies_to: 'provider' },
  { code: 'contribution_notice', label: 'Contribution notice', applies_to: 'client' },
  { code: 'payslip', label: 'Payslip', applies_to: 'worker' },
  { code: 'payroll', label: 'Payroll', applies_to: 'worker' },
  { code: 'staff_pay_document', label: 'Staff pay document', applies_to: 'worker' },
] as const;

export const DOCUMENT_SUBJECT_REGISTRY: readonly DocumentVocabularyRegistryRow[] = [
  { code: 'unclassified', label: 'Unclassified (needs triage)', applies_to: 'any' },
  { code: 'client', label: 'Client', applies_to: 'client' },
  {
    code: 'residential_accommodation_agreement',
    label: 'Residential accommodation agreement',
    applies_to: 'client',
  },
  {
    code: 'prudential_compliance_statement',
    label: 'Prudential compliance statement',
    applies_to: 'provider',
  },
  { code: 'organisation', label: 'Organisation', applies_to: 'provider' },
  {
    code: 'residential_admission_intake',
    label: 'Residential admission intake',
    applies_to: 'client',
  },
  { code: 'policy_register', label: 'Policy register', applies_to: 'provider' },
  { code: 'care_plan', label: 'Care plan', applies_to: 'client' },
  { code: 'staff', label: 'Staff', applies_to: 'worker' },
  { code: 'leave_request', label: 'Leave request', applies_to: 'worker' },
  { code: 'governance_attestation', label: 'Governance attestation', applies_to: 'provider' },
  { code: 'contractor', label: 'Contractor', applies_to: 'provider' },
  { code: 'insurance', label: 'Insurance', applies_to: 'provider' },
  { code: 'contract', label: 'Contract', applies_to: 'provider' },
  { code: 'certification', label: 'Certification', applies_to: 'provider' },
  { code: 'incident', label: 'Incident', applies_to: 'client' },
  { code: 'claim', label: 'Claim', applies_to: 'provider' },
  { code: 'service_agreement', label: 'Service agreement', applies_to: 'client' },
  {
    code: 'prudential_refund_deduction',
    label: 'Prudential refund deduction',
    applies_to: 'provider',
  },
  { code: 'sah_monthly_statement', label: 'SAH monthly statement', applies_to: 'client' },
  {
    code: 'prudential_external_submission',
    label: 'Prudential external submission',
    applies_to: 'provider',
  },
  { code: 'invoice', label: 'Invoice', applies_to: 'provider' },
  { code: 'prudential_disclosure', label: 'Prudential disclosure', applies_to: 'provider' },
  { code: 'residential_billing_run', label: 'Residential billing run', applies_to: 'provider' },
  { code: 'telehealth_session', label: 'Telehealth session', applies_to: 'client' },
  { code: 'complaint', label: 'Complaint', applies_to: 'client' },
  { code: 'tenant', label: 'Tenant', applies_to: 'provider' },
] as const;

// ─── Alias seed (live probe + workarounds + mobile allowlist + RLS tokens) ──
// Every live value from `.a2-live-vocabulary.txt` must appear here, mapped to a
// canonical code or to UNCLASSIFIED. Additional non-live tokens exist so dual-read
// queries and the mobile allowlist remain a SUPERSET of today's behaviour.

export const DOCUMENT_VOCABULARY_ALIASES: readonly DocumentVocabularyAliasRow[] = [
  // ── category: 44 live ──
  { kind: 'category', variant: 'clinical', code: 'clinical' },
  { kind: 'category', variant: 'client', code: 'client' },
  { kind: 'category', variant: 'Compliance & Audit', code: 'compliance_audit' },
  { kind: 'category', variant: 'prudential', code: 'prudential' },
  { kind: 'category', variant: 'Clinical Templates', code: 'clinical_templates' },
  {
    kind: 'category',
    variant: 'prudential-compliance-statement',
    code: 'prudential_compliance_statement',
  },
  {
    kind: 'category',
    variant: 'residential-accommodation-agreement',
    code: 'residential_accommodation_agreement',
  },
  { kind: 'category', variant: 'residential_admission', code: 'residential_admission' },
  { kind: 'category', variant: 'historical', code: 'historical' },
  { kind: 'category', variant: 'assessment', code: 'assessment' },
  { kind: 'category', variant: 'funding', code: 'funding' },
  {
    kind: 'category',
    variant: 'residential-admission-agreement',
    code: 'residential_admission_agreement',
  },
  { kind: 'category', variant: 'prudential-governance', code: 'prudential_governance' },
  { kind: 'category', variant: 'care_plan_active_version', code: 'care_plan_active_version' },
  { kind: 'category', variant: 'consent', code: 'consent' },
  { kind: 'category', variant: 'care-plan', code: 'care_plan' },
  { kind: 'category', variant: 'Policies & Procedures', code: 'policies_and_procedures' },
  { kind: 'category', variant: 'worker-credential-renewal', code: 'worker_credential_renewal' },
  { kind: 'category', variant: 'leave-request-evidence', code: 'leave_request_evidence' },
  { kind: 'category', variant: 'WHS', code: 'whs' },
  { kind: 'category', variant: 'signed-document', code: 'signed_document' },
  { kind: 'category', variant: 'Policies', code: 'policy' },
  { kind: 'category', variant: 'Care Plans', code: 'care_plan' },
  { kind: 'category', variant: 'contract', code: 'contract' },
  { kind: 'category', variant: 'sah_statement', code: 'sah_statement' },
  { kind: 'category', variant: 'Clinical', code: 'clinical' },
  { kind: 'category', variant: 'Insurance', code: 'insurance' },
  { kind: 'category', variant: 'Vendor Contracts', code: 'vendor_contracts' },
  { kind: 'category', variant: 'Financial', code: 'financial' },
  { kind: 'category', variant: 'Governance', code: 'governance' },
  { kind: 'category', variant: 'Statements', code: 'statement' },
  { kind: 'category', variant: 'Procedures', code: 'procedure' },
  { kind: 'category', variant: 'Invoices', code: 'invoice' },
  { kind: 'category', variant: 'service_agreement', code: 'service_agreement' },
  { kind: 'category', variant: 'Human Resources', code: 'human_resources' },
  { kind: 'category', variant: 'Quality & Safety', code: 'quality_and_safety' },
  { kind: 'category', variant: 'Care Plan', code: 'care_plan' },
  { kind: 'category', variant: 'Registration', code: 'registration' },
  { kind: 'category', variant: 'Staff Credentials', code: 'staff_credentials' },
  { kind: 'category', variant: 'incident', code: 'incident' },
  { kind: 'category', variant: 'PROFESSIONAL_INDEMNITY', code: 'professional_indemnity' },
  { kind: 'category', variant: 'Incident Reports', code: 'incident_report' },
  { kind: 'category', variant: 'Assessments', code: 'assessment' },
  { kind: 'category', variant: 'Finance', code: 'financial' },
  { kind: 'category', variant: 'incident_evidence', code: 'incident_evidence' },

  // category: compliance-trial RLS tokens (may have zero live rows today)
  { kind: 'category', variant: 'audit', code: 'audit' },
  { kind: 'category', variant: 'compliance', code: 'compliance' },
  { kind: 'category', variant: 'complaint', code: 'complaint' },
  { kind: 'category', variant: 'governance', code: 'governance' },
  { kind: 'category', variant: 'policy', code: 'policy' },
  { kind: 'category', variant: 'quality', code: 'quality' },

  // category: retired queries.ts care-plan document list
  { kind: 'category', variant: 'individualised_budget', code: 'individualised_budget' },
  {
    kind: 'category',
    variant: 'individualised_budget_active_version',
    code: 'individualised_budget_active_version',
  },
  { kind: 'category', variant: 'sah_individualised_budget', code: 'sah_individualised_budget' },
  { kind: 'category', variant: 'care_plan_copy_issued', code: 'care_plan_copy_issued' },
  { kind: 'category', variant: 'care_plan_participant_copy', code: 'care_plan_participant_copy' },
  { kind: 'category', variant: 'care_plan_copy_evidence', code: 'care_plan_copy_evidence' },

  // category: policy_register dual-read + common casing
  { kind: 'category', variant: 'FINANCIAL', code: 'financial' },
  { kind: 'category', variant: 'GOVERNANCE', code: 'governance' },

  // category: mobile worker allowlist (G23 clinical point-of-care)
  { kind: 'category', variant: 'acd', code: 'advance_care_directive' },
  { kind: 'category', variant: 'advance_care_directive', code: 'advance_care_directive' },
  { kind: 'category', variant: 'advance_care_directives', code: 'advance_care_directive' },
  { kind: 'category', variant: 'advance_care', code: 'advance_care_directive' },
  { kind: 'category', variant: 'Advance Care Directive', code: 'advance_care_directive' },
  { kind: 'category', variant: 'goals_of_care', code: 'goals_of_care' },
  { kind: 'category', variant: 'epoa', code: 'epoa' },
  { kind: 'category', variant: 'bsp', code: 'behaviour_support_plan' },
  { kind: 'category', variant: 'behaviour_support_plan', code: 'behaviour_support_plan' },
  { kind: 'category', variant: 'behavior_support_plan', code: 'behaviour_support_plan' },
  { kind: 'category', variant: 'behaviour_support', code: 'behaviour_support_plan' },
  { kind: 'category', variant: 'behavior_support', code: 'behaviour_support_plan' },
  { kind: 'category', variant: 'wound_protocol', code: 'wound_protocol' },
  { kind: 'category', variant: 'wound_care_protocol', code: 'wound_protocol' },
  { kind: 'category', variant: 'wound_care', code: 'wound_protocol' },
  { kind: 'category', variant: 'wound_protocol_pdf', code: 'wound_protocol' },
  { kind: 'category', variant: 'service_agreements', code: 'service_agreement' },
  { kind: 'category', variant: 'sah_service_agreement', code: 'service_agreement' },
  { kind: 'category', variant: 'signed_service_agreement', code: 'service_agreement' },

  // category: pay blocklist tokens (so resolve does not invent UNCLASSIFIED for them)
  { kind: 'category', variant: 'payslip', code: 'payslip' },
  { kind: 'category', variant: 'pay_slip', code: 'payslip' },
  { kind: 'category', variant: 'payroll', code: 'payroll' },
  { kind: 'category', variant: 'pay_document', code: 'staff_pay_document' },
  { kind: 'category', variant: 'staff_pay_document', code: 'staff_pay_document' },
  { kind: 'category', variant: 'staff_pay', code: 'staff_pay_document' },
  { kind: 'category', variant: 'pay_advice', code: 'staff_pay_document' },
  { kind: 'category', variant: 'payroll_payslip', code: 'payslip' },
  { kind: 'category', variant: 'remuneration', code: 'staff_pay_document' },

  // ── document_type: 60 live ──
  { kind: 'document_type', variant: 'clinical_document', code: 'clinical_document' },
  {
    kind: 'document_type',
    variant: 'SIGNED_ACCOMMODATION_AGREEMENT',
    code: 'signed_accommodation_agreement',
  },
  { kind: 'document_type', variant: 'client_document', code: 'client_document' },
  { kind: 'document_type', variant: 'care_plan', code: 'care_plan' },
  {
    kind: 'document_type',
    variant: 'PrudentialComplianceStatement',
    code: 'prudential_compliance_statement',
  },
  { kind: 'document_type', variant: 'resident_agreement', code: 'resident_agreement' },
  {
    kind: 'document_type',
    variant: 'residential_admission_agreement',
    code: 'residential_admission_agreement',
  },
  { kind: 'document_type', variant: 'advance_care_directive', code: 'advance_care_directive' },
  { kind: 'document_type', variant: 'service_agreement', code: 'service_agreement' },
  { kind: 'document_type', variant: 'individualised_budget', code: 'individualised_budget' },
  {
    kind: 'document_type',
    variant: 'RESIDENTIAL_ADMISSION_AGREEMENT',
    code: 'residential_admission_agreement',
  },
  { kind: 'document_type', variant: 'policy', code: 'policy' },
  { kind: 'document_type', variant: 'historical_consent', code: 'historical_consent' },
  { kind: 'document_type', variant: 'assessment', code: 'assessment' },
  { kind: 'document_type', variant: 'support_plan', code: 'support_plan' },
  {
    kind: 'document_type',
    variant: 'historical_service_agreement',
    code: 'historical_service_agreement',
  },
  { kind: 'document_type', variant: 'CARE_PLAN_ACTIVE_VERSION', code: 'care_plan_active_version' },
  { kind: 'document_type', variant: 'GOVERNANCE_POLICY', code: 'governance_policy' },
  { kind: 'document_type', variant: 'consent', code: 'consent' },
  {
    kind: 'document_type',
    variant: 'PRUDENTIAL_POLICY_ARTIFACT',
    code: 'prudential_policy_artifact',
  },
  { kind: 'document_type', variant: 'home_risk_assessment', code: 'home_risk_assessment' },
  { kind: 'document_type', variant: 'VEHICLE_INSURANCE', code: 'vehicle_insurance' },
  { kind: 'document_type', variant: 'SIGNED_DOCUMENT', code: 'signed_document' },
  { kind: 'document_type', variant: 'contractor_agreement', code: 'contractor_agreement' },
  { kind: 'document_type', variant: 'PRUDENTIAL_EVIDENCE_PACK', code: 'prudential_evidence_pack' },
  { kind: 'document_type', variant: 'POLICY', code: 'policy' },
  { kind: 'document_type', variant: 'SAH_MONTHLY_STATEMENT', code: 'sah_monthly_statement' },
  { kind: 'document_type', variant: 'insurance', code: 'insurance' },
  { kind: 'document_type', variant: 'contract', code: 'contract' },
  { kind: 'document_type', variant: 'certification', code: 'certification' },
  { kind: 'document_type', variant: 'STATEMENT', code: 'statement' },
  { kind: 'document_type', variant: 'report', code: 'report' },
  { kind: 'document_type', variant: 'procedure', code: 'procedure' },
  { kind: 'document_type', variant: 'PRUDENTIAL_ATTESTATION', code: 'prudential_attestation' },
  { kind: 'document_type', variant: 'INVOICE', code: 'invoice' },
  {
    kind: 'document_type',
    variant: 'REFUND_DEDUCTION_EVIDENCE',
    code: 'refund_deduction_evidence',
  },
  { kind: 'document_type', variant: 'leave_evidence', code: 'leave_evidence' },
  { kind: 'document_type', variant: 'sds', code: 'sds' },
  { kind: 'document_type', variant: 'Care Plan Summary', code: 'care_plan_summary' },
  { kind: 'document_type', variant: 'SAH Budget Statement', code: 'sah_budget_statement' },
  { kind: 'document_type', variant: 'registration_certificate', code: 'registration_certificate' },
  { kind: 'document_type', variant: 'certificate', code: 'certificate' },
  { kind: 'document_type', variant: 'template', code: 'template' },
  { kind: 'document_type', variant: 'FINAL_REPORT', code: 'final_report' },
  { kind: 'document_type', variant: 'Advance Care Directive', code: 'advance_care_directive' },
  { kind: 'document_type', variant: 'CARE_PLAN', code: 'care_plan' },
  { kind: 'document_type', variant: 'PROFESSIONAL_INDEMNITY', code: 'professional_indemnity' },
  { kind: 'document_type', variant: 'incident_report', code: 'incident_report' },
  { kind: 'document_type', variant: 'compliance_document', code: 'compliance_document' },
  {
    kind: 'document_type',
    variant: 'PRUDENTIAL_STATEMENT_PACKAGE',
    code: 'prudential_statement_package',
  },
  { kind: 'document_type', variant: 'ASSESSMENT', code: 'assessment' },
  {
    kind: 'document_type',
    variant: 'EXTERNAL_SUBMISSION_EVIDENCE',
    code: 'external_submission_evidence',
  },
  { kind: 'document_type', variant: 'REMITTANCE', code: 'remittance' },
  {
    kind: 'document_type',
    variant: 'ANNUAL_PRUDENTIAL_STATEMENT',
    code: 'annual_prudential_statement',
  },
  {
    kind: 'document_type',
    variant: 'RESPITE_SERVICE_AGREEMENT',
    code: 'respite_service_agreement',
  },
  { kind: 'document_type', variant: 'CHSP_SERVICE_AGREEMENT', code: 'chsp_service_agreement' },
  { kind: 'document_type', variant: 'FINANCIAL', code: 'financial' },
  {
    kind: 'document_type',
    variant: 'SIRS_FINAL_REPORT_EVIDENCE',
    code: 'sirs_final_report_evidence',
  },
  { kind: 'document_type', variant: 'BOARD_ATTESTATION', code: 'board_attestation' },
  { kind: 'document_type', variant: 'PROCEDURE', code: 'procedure' },
  { kind: 'document_type', variant: 'contribution_notice', code: 'contribution_notice' },

  // ── entity_type: 31 live ──
  { kind: 'entity_type', variant: 'Client', code: 'client' },
  { kind: 'entity_type', variant: 'CLIENT', code: 'client' },
  {
    kind: 'entity_type',
    variant: 'residential_accommodation_agreements',
    code: 'residential_accommodation_agreement',
  },
  {
    kind: 'entity_type',
    variant: 'PrudentialComplianceStatement',
    code: 'prudential_compliance_statement',
  },
  {
    kind: 'entity_type',
    variant: 'ResidentialAccommodationAgreement',
    code: 'residential_accommodation_agreement',
  },
  { kind: 'entity_type', variant: 'client', code: 'client' },
  { kind: 'entity_type', variant: 'organisation', code: 'organisation' },
  {
    kind: 'entity_type',
    variant: 'ResidentialAdmissionIntake',
    code: 'residential_admission_intake',
  },
  { kind: 'entity_type', variant: 'policy_register', code: 'policy_register' },
  { kind: 'entity_type', variant: 'care_plan', code: 'care_plan' },
  { kind: 'entity_type', variant: 'Staff', code: 'staff' },
  { kind: 'entity_type', variant: 'GovernanceAttestation', code: 'governance_attestation' },
  { kind: 'entity_type', variant: 'PolicyRegister', code: 'policy_register' },
  { kind: 'entity_type', variant: 'policy', code: 'policy_register' },
  { kind: 'entity_type', variant: 'contractor', code: 'contractor' },
  { kind: 'entity_type', variant: 'insurance', code: 'insurance' },
  { kind: 'entity_type', variant: 'contract', code: 'contract' },
  { kind: 'entity_type', variant: 'certification', code: 'certification' },
  { kind: 'entity_type', variant: 'Incident', code: 'incident' },
  { kind: 'entity_type', variant: 'resident', code: 'client' },
  { kind: 'entity_type', variant: 'CLAIM', code: 'claim' },
  { kind: 'entity_type', variant: 'service_agreement', code: 'service_agreement' },
  {
    kind: 'entity_type',
    variant: 'prudential_refund_deductions',
    code: 'prudential_refund_deduction',
  },
  { kind: 'entity_type', variant: 'SAH_MONTHLY_STATEMENT', code: 'sah_monthly_statement' },
  { kind: 'entity_type', variant: 'CONTRACTOR', code: 'contractor' },
  {
    kind: 'entity_type',
    variant: 'prudential_external_submissions',
    code: 'prudential_external_submission',
  },
  {
    kind: 'entity_type',
    variant: 'prudential_compliance_statements',
    code: 'prudential_compliance_statement',
  },
  { kind: 'entity_type', variant: 'INVOICE', code: 'invoice' },
  { kind: 'entity_type', variant: 'PRUDENTIAL_DISCLOSURE', code: 'prudential_disclosure' },
  { kind: 'entity_type', variant: 'RESIDENTIAL_BILLING_RUN', code: 'residential_billing_run' },
  { kind: 'entity_type', variant: 'governance_attestations', code: 'governance_attestation' },

  // entity_type: retired queries.ts workarounds + compliance-trial RLS tokens
  { kind: 'entity_type', variant: 'CARE_PLAN', code: 'care_plan' },
  { kind: 'entity_type', variant: 'CarePlan', code: 'care_plan' },
  { kind: 'entity_type', variant: 'INCIDENT', code: 'incident' },
  { kind: 'entity_type', variant: 'telehealth_session', code: 'telehealth_session' },
  { kind: 'entity_type', variant: 'TELEHEALTH_SESSION', code: 'telehealth_session' },
  { kind: 'entity_type', variant: 'telehealth', code: 'telehealth_session' },
  { kind: 'entity_type', variant: 'Complaint', code: 'complaint' },
  { kind: 'entity_type', variant: 'Tenant', code: 'tenant' },
] as const;

// ─── Live-probe inventory (for tests; counts must match .a2-live-vocabulary.txt) ──

export const LIVE_CATEGORY_VALUES = [
  'clinical',
  'client',
  'Compliance & Audit',
  'prudential',
  'Clinical Templates',
  'prudential-compliance-statement',
  'residential-accommodation-agreement',
  'residential_admission',
  'historical',
  'assessment',
  'funding',
  'residential-admission-agreement',
  'prudential-governance',
  'care_plan_active_version',
  'consent',
  'care-plan',
  'Policies & Procedures',
  'worker-credential-renewal',
  'WHS',
  'signed-document',
  'Policies',
  'Care Plans',
  'contract',
  'sah_statement',
  'Clinical',
  'Insurance',
  'Vendor Contracts',
  'Financial',
  'Governance',
  'Statements',
  'Procedures',
  'Invoices',
  'service_agreement',
  'Human Resources',
  'Quality & Safety',
  'Care Plan',
  'Registration',
  'Staff Credentials',
  'incident',
  'PROFESSIONAL_INDEMNITY',
  'Incident Reports',
  'Assessments',
  'Finance',
  'incident_evidence',
] as const;

export const LIVE_DOCUMENT_TYPE_VALUES = [
  'clinical_document',
  'SIGNED_ACCOMMODATION_AGREEMENT',
  'client_document',
  'care_plan',
  'PrudentialComplianceStatement',
  'resident_agreement',
  'residential_admission_agreement',
  'advance_care_directive',
  'service_agreement',
  'individualised_budget',
  'RESIDENTIAL_ADMISSION_AGREEMENT',
  'policy',
  'historical_consent',
  'assessment',
  'support_plan',
  'historical_service_agreement',
  'CARE_PLAN_ACTIVE_VERSION',
  'GOVERNANCE_POLICY',
  'consent',
  'PRUDENTIAL_POLICY_ARTIFACT',
  'home_risk_assessment',
  'VEHICLE_INSURANCE',
  'SIGNED_DOCUMENT',
  'contractor_agreement',
  'PRUDENTIAL_EVIDENCE_PACK',
  'POLICY',
  'SAH_MONTHLY_STATEMENT',
  'insurance',
  'contract',
  'certification',
  'STATEMENT',
  'report',
  'procedure',
  'PRUDENTIAL_ATTESTATION',
  'INVOICE',
  'REFUND_DEDUCTION_EVIDENCE',
  'sds',
  'Care Plan Summary',
  'SAH Budget Statement',
  'registration_certificate',
  'certificate',
  'template',
  'FINAL_REPORT',
  'Advance Care Directive',
  'CARE_PLAN',
  'PROFESSIONAL_INDEMNITY',
  'incident_report',
  'compliance_document',
  'PRUDENTIAL_STATEMENT_PACKAGE',
  'ASSESSMENT',
  'EXTERNAL_SUBMISSION_EVIDENCE',
  'REMITTANCE',
  'ANNUAL_PRUDENTIAL_STATEMENT',
  'RESPITE_SERVICE_AGREEMENT',
  'CHSP_SERVICE_AGREEMENT',
  'FINANCIAL',
  'SIRS_FINAL_REPORT_EVIDENCE',
  'BOARD_ATTESTATION',
  'PROCEDURE',
  'contribution_notice',
] as const;

export const LIVE_ENTITY_TYPE_VALUES = [
  'Client',
  'CLIENT',
  'residential_accommodation_agreements',
  'PrudentialComplianceStatement',
  'ResidentialAccommodationAgreement',
  'client',
  'organisation',
  'ResidentialAdmissionIntake',
  'policy_register',
  'care_plan',
  'Staff',
  'GovernanceAttestation',
  'PolicyRegister',
  'policy',
  'contractor',
  'insurance',
  'contract',
  'certification',
  'Incident',
  'resident',
  'CLAIM',
  'service_agreement',
  'prudential_refund_deductions',
  'SAH_MONTHLY_STATEMENT',
  'CONTRACTOR',
  'prudential_external_submissions',
  'prudential_compliance_statements',
  'INVOICE',
  'PRUDENTIAL_DISCLOSURE',
  'RESIDENTIAL_BILLING_RUN',
  'governance_attestations',
] as const;

/** Retired hardcoded lists — documentTokenVariants must remain a SUPERSET. */
export const RETIRED_WORKAROUND_LISTS = {
  carePlanEntityTypes: ['care_plan', 'CARE_PLAN', 'CarePlan'] as const,
  carePlanCategories: [
    'care_plan_active_version',
    'individualised_budget',
    'individualised_budget_active_version',
    'sah_individualised_budget',
    'care_plan_copy_issued',
    'care_plan_participant_copy',
    'care_plan_copy_evidence',
  ] as const,
  incidentEntityTypes: ['INCIDENT', 'Incident'] as const,
  telehealthEntityTypes: ['telehealth_session', 'TELEHEALTH_SESSION', 'telehealth'] as const,
  prudentialPolicyCategories: ['FINANCIAL', 'GOVERNANCE'] as const,
  workerRelevantCategories: [
    'acd',
    'advance_care_directive',
    'advance_care_directives',
    'advance_care',
    'goals_of_care',
    'epoa',
    'bsp',
    'behaviour_support_plan',
    'behavior_support_plan',
    'behaviour_support',
    'behavior_support',
    'wound_protocol',
    'wound_care_protocol',
    'wound_care',
    'wound_protocol_pdf',
    'service_agreement',
    'service_agreements',
    'sah_service_agreement',
    'signed_service_agreement',
  ] as const,
  complianceTrialCategories: [
    'audit',
    'client',
    'compliance',
    'complaint',
    'consent',
    'governance',
    'incident',
    'policy',
    'quality',
    'signed-document',
  ] as const,
  complianceTrialEntityTypes: ['Client', 'Complaint', 'Incident', 'Tenant'] as const,
} as const;

// ─── Normalisation + resolution ─────────────────────────────────────────────

/**
 * Single normalisation for dual-read matching: trim, case-fold, collapse
 * spaces / underscores / hyphens. Matches the SQL resolve_document_vocabulary
 * key form.
 */
export function normaliseDocumentVocabularyKey(value: string | null | undefined): string {
  if (value == null) return '';
  return value
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '');
}

function buildAliasIndex(): Map<
  DocumentVocabularyKind,
  Map<string, { code: string; variants: string[] }>
> {
  const byKind = new Map<
    DocumentVocabularyKind,
    Map<string, { code: string; variants: string[] }>
  >();
  for (const kind of ['category', 'document_type', 'entity_type'] as const) {
    byKind.set(kind, new Map());
  }

  // code → variants per kind
  const variantsByCode = new Map<string, string[]>();
  const codeByKey = new Map<string, string>();

  for (const row of DOCUMENT_VOCABULARY_ALIASES) {
    const key = normaliseDocumentVocabularyKey(row.variant);
    if (!key) continue;
    const composite = `${row.kind}::${key}`;
    // First alias wins for a normalised key; identical mappings are fine.
    if (!codeByKey.has(composite)) {
      codeByKey.set(composite, row.code);
    }
    const vk = `${row.kind}::${row.code}`;
    const list = variantsByCode.get(vk) ?? [];
    if (!list.includes(row.variant)) list.push(row.variant);
    variantsByCode.set(vk, list);
  }

  for (const kind of ['category', 'document_type', 'entity_type'] as const) {
    const keyMap = byKind.get(kind)!;
    for (const [composite, code] of codeByKey) {
      if (!composite.startsWith(`${kind}::`)) continue;
      const key = composite.slice(kind.length + 2);
      const variants = variantsByCode.get(`${kind}::${code}`) ?? [];
      keyMap.set(key, { code, variants });
    }
    // Also index by code for reverse lookup of variants
    for (const [vk, variants] of variantsByCode) {
      if (!vk.startsWith(`${kind}::`)) continue;
      const code = vk.slice(kind.length + 2);
      // Store under a sentinel so documentTokenVariants can find by code
      keyMap.set(`__code__${code}`, { code, variants });
    }
  }

  return byKind;
}

const ALIAS_INDEX = buildAliasIndex();

function registryCodes(kind: DocumentVocabularyKind): Set<string> {
  const rows = kind === 'entity_type' ? DOCUMENT_SUBJECT_REGISTRY : DOCUMENT_TYPE_REGISTRY;
  return new Set(rows.map((r) => r.code));
}

/**
 * Resolve any free-text variant to its canonical UPPER_SNAKE code, or UNCLASSIFIED.
 * Empty / null input returns UNCLASSIFIED (explicit — never silent null).
 */
export function canonicaliseDocumentToken(
  value: string | null | undefined,
  kind: DocumentVocabularyKind,
): string {
  const key = normaliseDocumentVocabularyKey(value);
  if (!key) return DOCUMENT_VOCABULARY_UNCLASSIFIED;

  const entry = ALIAS_INDEX.get(kind)?.get(key);
  if (entry) return entry.code;

  // Accept already-canonical codes that are registered but have no separate alias row.
  // Canonical is lower_snake_case: the live vocabulary tables carry
  // CHECK (code = lower(code)), so an UPPER code cannot exist to be matched.
  const canonical = (value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
  if (registryCodes(kind).has(canonical)) return canonical;

  return DOCUMENT_VOCABULARY_UNCLASSIFIED;
}

/**
 * Every known free-text variant for a canonical code (plus the code itself).
 * Use with `.in(column, documentTokenVariants(...))` while free text remains
 * authoritative. Always a SUPERSET of the retired hardcoded lists for that code.
 */
export function documentTokenVariants(
  canonicalCode: string,
  kind: DocumentVocabularyKind,
): string[] {
  const code = (canonicalCode ?? '').trim().toLowerCase();
  if (!code) return [];

  const fromIndex = ALIAS_INDEX.get(kind)?.get(`__code__${code}`);
  const variants = fromIndex?.variants ? [...fromIndex.variants] : [];

  // Always include the canonical form itself for dual-read forward-compat.
  if (!variants.includes(code)) variants.push(code);

  // Stable order: original alias order then canonical if new.
  return variants;
}

/**
 * Union of variants across several canonical codes (deduped, stable).
 */
export function documentTokenVariantsForCodes(
  canonicalCodes: readonly string[],
  kind: DocumentVocabularyKind,
): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const code of canonicalCodes) {
    for (const v of documentTokenVariants(code, kind)) {
      if (seen.has(v)) continue;
      seen.add(v);
      out.push(v);
    }
  }
  return out;
}

/**
 * Expand a list of free-text tokens (e.g. a retired workaround array) to the
 * full dual-read variant set for their resolved codes. Guarantees SUPERSET of
 * the input list when every input has an alias.
 */
export function expandDocumentTokenList(
  values: readonly string[],
  kind: DocumentVocabularyKind,
): string[] {
  const codes = new Set<string>();
  for (const v of values) {
    codes.add(canonicaliseDocumentToken(v, kind));
  }
  const expanded = documentTokenVariantsForCodes([...codes], kind);
  // Guarantee input membership even if a token was somehow unmapped.
  const seen = new Set(expanded);
  for (const v of values) {
    if (!seen.has(v)) {
      expanded.push(v);
      seen.add(v);
    }
  }
  return expanded;
}

/** Live values that resolve to UNCLASSIFIED for a kind (for the slice report / tests). */
export function liveUnclassifiedValues(kind: DocumentVocabularyKind): string[] {
  const live =
    kind === 'category'
      ? LIVE_CATEGORY_VALUES
      : kind === 'document_type'
        ? LIVE_DOCUMENT_TYPE_VALUES
        : LIVE_ENTITY_TYPE_VALUES;
  return live.filter(
    (v) => canonicaliseDocumentToken(v, kind) === DOCUMENT_VOCABULARY_UNCLASSIFIED,
  );
}

/**
 * Worker-relevant category codes at point of care (G23). Mobile allowlist is
 * derived from variants of these codes so ACD / BSP casing variants are visible.
 */
export const WORKER_RELEVANT_CATEGORY_CODES = [
  'advance_care_directive',
  'goals_of_care',
  'epoa',
  'behaviour_support_plan',
  'wound_protocol',
  'service_agreement',
] as const;

export const ACD_CATEGORY_CODES = ['advance_care_directive', 'goals_of_care', 'epoa'] as const;

export const PAY_DOCUMENT_CATEGORY_CODES = ['payslip', 'payroll', 'staff_pay_document'] as const;

/** SQL-safe single-quoted literal. */
export function sqlQuote(value: string): string {
  return `'${value.replace(/'/g, "''")}'`;
}
